import React, { useState, useMemo } from 'react';
import { cn } from '../../../lib/utils';

/**
 * ZoneOverlay
 * ------------
 * Layer placed exactly on top of the iframe. Zone x/y come from
 * element.getBoundingClientRect() inside the iframe document, which is
 * already relative to the iframe's own viewport — which is the same
 * coordinate space this absolute-positioned overlay lives in.
 * So we use coords directly, no offset math.
 */
const ZoneOverlay = ({
  zones = [],
  mode = 'overview',
  activeStep = null,
  onZoneClick,
  disabledGroups = new Set(),
}) => {
  const [hoverId, setHoverId] = useState(null);

  const activeTestid = useMemo(() => {
    if (!activeStep) return null;
    if (zones.some((z) => z.testid === activeStep.testid)) return activeStep.testid;
    if (
      activeStep.fallbackTestid &&
      zones.some((z) => z.testid === activeStep.fallbackTestid)
    ) {
      return activeStep.fallbackTestid;
    }
    return activeStep.testid;
  }, [activeStep, zones]);

  // Compute which zones should render based on mode + group toggles.
  const visibleZones = useMemo(() => {
    return zones.filter((z) => {
      if (mode === 'drilldown') {
        // Only the active zone in drill-down.
        return z.testid === activeTestid;
      }
      // Overview mode — respect group toggles.
      const group = groupOf(z.testid);
      return !disabledGroups.has(group);
    });
  }, [zones, mode, activeTestid, disabledGroups]);

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      data-testid="visual-lab-zone-overlay"
    >
      {mode === 'drilldown' && activeTestid && (
        <div className="absolute inset-0 bg-background/55 backdrop-blur-[1px] pointer-events-none" />
      )}

      {visibleZones.map((z, i) => {
        const isActive = activeTestid === z.testid;
        const isHover = hoverId === `${z.testid}-${i}`;

        const style = {
          position: 'absolute',
          left: `${z.x}px`,
          top: `${z.y}px`,
          width: `${z.w}px`,
          height: `${z.h}px`,
        };

        return (
          <button
            key={`${z.testid}-${i}`}
            type="button"
            style={style}
            onMouseEnter={() => setHoverId(`${z.testid}-${i}`)}
            onMouseLeave={() => setHoverId(null)}
            onClick={(e) => {
              e.preventDefault();
              onZoneClick?.(z);
            }}
            className={cn(
              'pointer-events-auto rounded-[3px] transition-colors duration-100',
              'focus:outline-none',
              isActive
                ? 'ring-2 ring-violet-500 bg-violet-500/10'
                : isHover
                ? 'ring-2 ring-violet-500 bg-violet-500/10'
                : 'ring-1 ring-violet-400/70 hover:ring-2 hover:ring-violet-500 hover:bg-violet-500/10'
            )}
            data-testid={`visual-lab-zone-${z.testid}`}
          >
            {/* Testid label — only shown on hover / active to avoid clutter */}
            {(isHover || isActive) && (
              <span
                className="absolute left-0 top-0 -translate-y-full px-1.5 py-0.5 rounded-t text-[9px] font-mono whitespace-nowrap max-w-[260px] truncate bg-violet-600 text-white shadow-sm"
                style={{ marginTop: '-1px' }}
              >
                {z.testid}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Derive a display group name from a testid. Everything before the first
 * hyphen-segment that looks like an id becomes the group; fall back to the
 * first two segments for hierarchical testids.
 *
 * Examples:
 *   dd2-task-row-abc123        → "dd2-task-row"
 *   dd2-pane-unplanned         → "dd2-pane"
 *   focus-strip-report         → "focus-strip"
 *   today-command-band         → "today-command"
 *   orka-task-header-xyz       → "orka-task-header"
 */
export const groupOf = (testid = '') => {
  if (!testid) return 'other';
  const parts = testid.split('-');
  if (parts.length <= 2) return testid;
  // Trim trailing id-looking segment (hex/random/numeric).
  const last = parts[parts.length - 1];
  const looksLikeId =
    /^[0-9a-f]{6,}$/i.test(last) ||
    /^\d+$/.test(last) ||
    (last.length >= 8 && /[0-9]/.test(last));
  const trimmed = looksLikeId ? parts.slice(0, -1) : parts;
  // Group by first 2–3 segments, whichever is more descriptive.
  return trimmed.slice(0, Math.min(3, trimmed.length)).join('-');
};

export default ZoneOverlay;
